import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { API_URL, formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { Loader2, Building2, User, Users } from 'lucide-react';

const AdminTabPrivatePractice = () => {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ total: 0, individual: 0, group: 0, published: 0 });
  const [freeLaunch, setFreeLaunch] = useState(true);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [list, cfg] = await Promise.all([
        axios.get(`${API_URL}/admin/private-practices`),
        axios.get(`${API_URL}/private-practices/config`),
      ]);
      setRows(list.data.practices || []);
      setCounts(list.data.counts || {});
      setFreeLaunch(cfg.data.free_launch_mode);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleLaunch = async (v) => {
    setFreeLaunch(v);
    try { await axios.put(`${API_URL}/admin/private-practice-config`, { free_launch_mode: v }); toast.success(`Free Launch Mode ${v ? 'ON' : 'OFF'}`); }
    catch (e) { toast.error('Failed to update setting.'); setFreeLaunch(!v); }
  };

  const setStatus = async (id, status) => {
    try {
      await axios.put(`${API_URL}/admin/private-practices/${id}/status`, { status });
      setRows((r) => r.map((p) => (p.practice_id === id ? { ...p, status } : p)));
      toast.success(status === 'active' ? 'Published' : 'Unpublished');
    } catch (e) { toast.error('Failed to update.'); }
  };

  if (loading) return <div className="flex items-center justify-center py-20 text-gray-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>;

  return (
    <div data-testid="admin-private-practice">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        {[['Total', counts.total, Building2], ['Individual', counts.individual, User], ['Group', counts.group, Users], ['Published', counts.published, Building2]].map(([label, val, Icon]) => (
          <div key={label} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center"><Icon className="w-4 h-4 text-primary" /></div>
            <div><div className="text-xl font-bold text-primary">{val ?? 0}</div><div className="text-xs text-gray-500">{label}</div></div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-4 mb-5 flex items-center justify-between" data-testid="pp-free-launch-row">
        <div>
          <p className="font-semibold text-primary">Free Launch Mode</p>
          <p className="text-sm text-gray-500">When ON, listings are free and paid tiers show as "Coming Soon".</p>
        </div>
        <Switch checked={freeLaunch} onCheckedChange={toggleLaunch} data-testid="pp-free-launch-switch" />
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Practice / Practitioner</th>
                <th>Contact Name</th>
                <th>Email</th>
                <th>Date Signed Up</th>
                <th>Type</th>
                <th>Published</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.practice_id} data-testid={`pp-admin-row-${p.practice_id}`}>
                  <td className="font-medium">{p.practice_name}</td>
                  <td>{p.contact_name || '—'}</td>
                  <td>{p.email}</td>
                  <td>{formatDate(p.created_at)}</td>
                  <td><Badge variant="outline">{p.practice_type === 'group' ? 'Group' : 'Individual'}</Badge></td>
                  <td>{p.status === 'active' ? <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Yes</Badge> : <Badge className="bg-gray-100 text-gray-600 hover:bg-gray-100">No</Badge>}</td>
                  <td>
                    {p.status === 'active'
                      ? <Button size="sm" variant="outline" className="text-red-600" onClick={() => setStatus(p.practice_id, 'unpublished')} data-testid={`pp-unpublish-${p.practice_id}`}>Unpublish</Button>
                      : <Button size="sm" variant="outline" className="text-green-700" onClick={() => setStatus(p.practice_id, 'active')} data-testid={`pp-publish-${p.practice_id}`}>Publish</Button>}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={7} className="text-center text-gray-400 py-10">No private practice listings yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminTabPrivatePractice;
